import { ChevronRightIcon, StopIcon } from "@storytell/bsky-icons";
import { proseMirrorToRichText } from "@storytell/rich-text";
import { isStreaming } from "@storytell/streaming";
import type { PmDoc } from "@storytell/types/richText";
import { TbArrowUpRight, TbMicrophone } from "solid-icons/tb";
import { type Component, Match, Show, Switch, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { ButtonCircleIcon } from "../../../ui";
import { usePromptBarContext } from "../PromptBarContext";
import { useSpeechRecognition } from "../hooks";
import { PromptBarButton } from "./PromptBarButton";

/**
 * Submit button that switches between microphone, submit arrow, and stop button based on input and streaming state
 */
export const SubmitButton: Component<{ onSubmit: () => void }> = (props) => {
  const context = usePromptBarContext();
  const wire = useWire();
  const [speechStart, setSpeechStart] = createSignal(0);

  // Speech recognition functionality
  const speech = useSpeechRecognition(
    // onTranscript: append new transcript to existing input
    (text, isFinal) => {
      const e = context.prompt.editor();
      if (!e) return;
      e.commands.deleteRange({
        from: speechStart(),
        to: e.state.selection.$from?.pos || 0,
      });
      e.commands.insertContent({ type: "text", text });
      if (isFinal) {
        setSpeechStart(e.state.selection.$from?.pos || 0);
      }

      context.input.setInputValue(e.getText().trim());
    },
  );

  const handleMicrophoneClick = () => {
    if (speech.speaking()) {
      speech.stop();
    } else {
      const e = context.prompt.editor();
      if (!e) return;
      setSpeechStart(e.state.selection.$from?.pos || 0);
      speech.start();
    }
  };

  const handleStopClick = () => {
    if (context.streaming?.onStopStreaming) {
      context.streaming.onStopStreaming();
    }
  };

  // Priority order: Stop button > Submit button > Microphone
  return (
    <>
      <Switch>
        <Match when={wire.chat.isChatStreaming()}>
          <PromptBarButton
            icon={StopIcon}
            class={wire.chat.isInterrupting() ? "text-orange-500 animate-pulse" : "text-red-500"}
            aria-label={wire.chat.isInterrupting() ? "Stopping..." : "Stop generation"}
            onClick={handleStopClick}
            disabled={wire.chat.isInterrupting()}
          />
        </Match>
        <Match when={context.input.hasInputText() && !speech.speaking()}>
          <PromptBarButton
            class="rounded-lg [&_svg]:stroke-3 text-on-brand bg-brand hover:bg-brand-hover"
            noBorder
            icon={TbArrowUpRight}
            aria-label="Submit"
            onClick={props.onSubmit}
          />
        </Match>
        <Match when={true}>
          <PromptBarButton
            icon={TbMicrophone}
            onClick={handleMicrophoneClick}
            class={
              speech.speaking()
                ? "text-white animate-pulse bg-red-500 dark:bg-red-500 hover:bg-red-500 dark:hover:bg-red-500  focus:ring-red-300 dark:focus:ring-red-800"
                : ""
            }
            aria-label={speech.speaking() ? "Stop voice input" : "Start voice input"}
          />
        </Match>
      </Switch>
    </>
  );
};
